// Token chip system for the Send Emails compose editor.
//
// Studio admins see friendly chips like [Student Name] in the editor.
// Internally each chip carries its Handlebars variable (e.g. {{studentFirstName}})
// in a `data-token` attribute. We convert chip <span> -> Handlebars before sending,
// and Handlebars -> chip <span> when loading a saved template.
//
// Chip styling uses literal hex (not CSS variables) because TinyMCE renders
// the editor inside a sandboxed iframe where `var(--dl-color-*)` does not resolve.

export const EMAIL_TOKENS = [
    { token: '[Student Name]', handlebarsVar: '{{studentFirstName}}', label: 'Student Name' },
    { token: '[Studio Name]', handlebarsVar: '{{orgName}}', label: 'Studio Name' },
    { token: '[Instructor Name]', handlebarsVar: '{{instructorName}}', label: 'Instructor Name' },
    { token: '[Class Name]', handlebarsVar: '{{className}}', label: 'Class Name' },
    { token: '[Term Name]', handlebarsVar: '{{termName}}', label: 'Term Name' },
    { token: '[Amount]', handlebarsVar: '{{formattedAmount}}', label: 'Amount' },
    { token: '[Service Fee]', handlebarsVar: '{{formattedServiceFee}}', label: 'Service Fee' },
    { token: '[Total Due]', handlebarsVar: '{{formattedTotalDue}}', label: 'Total Due' },
    { token: '[Currency]', handlebarsVar: '{{currency}}', label: 'Currency' },
    { token: '[Payment Link]', handlebarsVar: '{{paymentUrl}}', label: 'Payment Link' },
    { token: '[Class List]', handlebarsVar: '{{classListHtml}}', label: 'Class List' },
    { token: '[My Hub Link]', handlebarsVar: '{{myHubUrl}}', label: 'My Hub Link' },
    { token: '[Confirm Link]', handlebarsVar: '{{confirmUrl}}', label: 'Confirm Link' },
];

// Per-emailType allowed tokens — kept in sync with backend's
// `email-rendering.service.js → ALLOWED_TOKENS_PER_TYPE`. The Edit modal's
// chip picker filters by this map so admins only see the tokens that will
// actually resolve at send time.
const TOKENS_PER_TYPE = {
    classPayment: ['studentFirstName', 'orgName', 'formattedAmount', 'formattedServiceFee', 'formattedTotalDue', 'currency', 'paymentUrl', 'termName', 'classListHtml'],
    enrolmentPayment: ['studentFirstName', 'orgName', 'formattedAmount', 'formattedServiceFee', 'formattedTotalDue', 'currency', 'paymentUrl', 'classListHtml'],
    adjustmentPayment: ['studentFirstName', 'orgName', 'formattedAmount', 'formattedServiceFee', 'formattedTotalDue', 'currency', 'paymentUrl', 'adjustmentReason'],
    bookingPayment: ['studentFirstName', 'orgName', 'instructorName', 'bookingDate', 'startTime', 'endTime', 'locationDescription', 'formattedAmount', 'formattedServiceFee', 'formattedTotalDue', 'currency', 'paymentUrl'],
    rolloverNotification: ['studentFirstName', 'orgName', 'termName', 'classListHtml', 'myHubUrl'],
    paymentReceived: ['firstName', 'orgName', 'formattedAmount', 'paymentDescription'],
    bookingAdjustmentNotification: ['firstName', 'instructorName', 'orgName', 'formattedAmount', 'payUrl', 'oldVenue', 'newVenue', 'bookingDate', 'studioReason'],
    bookingAdjustmentRefundNotification: ['firstName', 'instructorName', 'orgName', 'formattedAmount', 'oldVenue', 'newVenue', 'bookingDate', 'studioReason'],
    broadcast: ['studentFirstName', 'orgName', 'className', 'termName'],
};

// Returns the EMAIL_TOKENS subset valid for a given emailType. Falls back to
// the broadcast set (the broadest neutral default) for unknown types.
export function tokensForEmailType(emailType) {
    const allowedVarNames = new Set(TOKENS_PER_TYPE[emailType] || TOKENS_PER_TYPE.broadcast);
    return EMAIL_TOKENS.filter(t => {
        const varName = t.handlebarsVar.replace(/\{\{|\}\}/g, '');
        return allowedVarNames.has(varName);
    });
}

// ─────────────────────────────────────────────────────────────
// MODE CLASSIFICATION (mirror of backend email.service.js getEmailMode)
// ─────────────────────────────────────────────────────────────
//
// Drives EmailReviewModal rendering branches. Backend remains source of
// truth for ownerInitiated; this map duplicates it so the UI can pick a
// rendering branch without an extra API call. If backend reclassifies a
// type, update this map alongside.

// When adding any new EMAIL_TYPES entry to backend email.service.js, also add it
// here (whichever Set matches its mode) and to the backend self-test
// _expectedClassification map. All three must stay in sync.
const OWNER_INITIATED = new Set(['rolloverNotification', 'classPayment', 'enrolmentPayment', 'adjustmentPayment', 'bookingPayment', 'paymentReceived', 'bookingAdjustmentNotification', 'bookingAdjustmentRefundNotification']);
const MODE_2A_TYPES = new Set(['eventCancellation']);
const MODE_2B_TYPES = new Set(['studentInvite']);

export function getEmailMode(emailType) {
    if (OWNER_INITIATED.has(emailType)) return 'mode3';
    if (MODE_2A_TYPES.has(emailType)) return 'mode2a';
    if (MODE_2B_TYPES.has(emailType)) return 'mode2b';
    return 'mode1';
}

export function buildChipHtml(handlebarsVar, label) {
    return `<span contenteditable="false" data-token="${handlebarsVar}" style="background:#f0eafc;color:#451f78;border-radius:4px;padding:1px 6px;font-size:0.875em;font-weight:500;white-space:nowrap;cursor:default;">[${label}]</span>`;
}

// Convert chip <span data-token="{{var}}">[Label]</span> -> {{var}} for sending.
// Targets the data-token attribute so display-text changes don't break extraction.
export function tokensToHandlebars(html) {
    return html.replace(/<span[^>]*data-token="([^"]*)"[^>]*>.*?<\/span>/gs, '$1');
}

// Convert Handlebars vars in a saved template into chips for editor display.
// Uses replaceAll so multiple occurrences of the same token all become chips.
export function handlebarsToTokens(html) {
    let result = html;
    for (const { handlebarsVar, label } of EMAIL_TOKENS) {
        const chipHtml = buildChipHtml(handlebarsVar, label);
        result = result.replaceAll(handlebarsVar, chipHtml);
    }
    return result;
}
